package com.elan.edubridge.ui.staff

import com.elan.edubridge.data.firebase.AttachmentStagingUploader
import com.elan.edubridge.data.firebase.AttachmentUploadResult
import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.Assignment
import com.elan.edubridge.shared.domain.model.AttachmentContentType
import com.elan.edubridge.shared.domain.model.ClassData
import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.shared.domain.model.CreateAssignmentRequest
import com.elan.edubridge.shared.domain.model.Permission
import com.elan.edubridge.shared.domain.model.PostAttachment
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.shared.domain.model.UpdateAssignmentRequest
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.icon
import com.elan.edubridge.ui.shell.readErrorHtml
import com.elan.edubridge.ui.shell.wireRetry
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement
import org.w3c.files.File

/**
 * 과제 — 학원이 자기 자료를 올려 학생에게 내보내는 화면.
 *
 * 과제 하나는 제목·마감일·대상 반, 그리고 **그 학원이 직접 올린 자료**(PDF·사진)다. 2026-09-09
 * 이전 설계는 EduMint 문제 은행에서 문항을 뽑는 것이었지만 중단됐다 —
 * `docs/product/PROBLEM_BANK_ASSIGNMENT_PLAN.md` §0 참조. 문제 은행 과제를 **만드는** 경로는
 * 그래서 이 화면에 없다.
 *
 * 자료 업로드는 게시판 첨부와 같은 거래를 쓴다: 브라우저가 자기 스테이징 경로에 올리고
 * (`AttachmentStagingUploader`) 서버가 실제 바이트를 검사해 과제로 승격한다. 파일을 여는 것은
 * 그때그때 받는 5분짜리 서명 URL이다 — Storage 규칙은 학원 멤버십을 판정할 수 없다.
 */
object AssignmentsPage {
    private val scope = MainScope()

    /** 과제 조회 실패의 "다시 시도" 버튼 id(#30). */
    private const val RETRY_ID = "staff-assignments-retry"

    private val repository = SharedGraph.createRepository()
    private val commandRepository = SharedGraph.createAcademyCommandRepository()

    /** 서버 `MAX_ATTACHMENTS_PER_ASSIGNMENT`와 같은 값이어야 한다. */
    private const val MAX_ATTACHMENTS = 5
    private const val MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024

    /** 반 이름을 붙이려면 반 목록이 필요하다 — 과제 목록과 함께 읽어 화면 하나로 다룬다. */
    private class PageData(
        val assignments: List<Assignment>,
        val classes: List<ClassData>,
        val failure: CommandFailure?,
    ) {
        fun className(classId: String?): String =
            classId?.let { id -> classes.firstOrNull { it.id == id }?.className ?: "삭제된 반" }
                ?: "학원 전체"
    }

    fun open(session: SessionState, academyId: String) {
        scope.launch {
            val assignments = repository.assignmentsResult(academyId)
            val classes = repository.classesResult(academyId)
            renderList(
                session,
                academyId,
                PageData(assignments.items, classes.items, assignments.failure),
            )
        }
    }

    private fun renderList(session: SessionState, academyId: String, data: PageData) {
        DashboardShell.render(session, academyId, "assignments", listHtml(session, data)) {
            wireRetry(RETRY_ID) { open(session, academyId) }
            wireList(session, academyId, data)
        }
    }

    private fun listHtml(session: SessionState, data: PageData): String {
        val canManage = session.activePermission.atLeast(Permission.EDITOR)
        val createButton = if (canManage) {
            """<button class="eb-button eb-button--primary" id="create-assignment" type="button">${icon("icon-plus", "sm")}과제 내기</button>"""
        } else {
            ""
        }
        // 못 읽은 것을 "과제가 없어요"로 그리면 스태프가 자기 과제가 사라진 줄 안다(#30).
        val list = when {
            data.assignments.isNotEmpty() -> data.assignments.joinToString("") { listItemHtml(it, data, canManage) }
            data.failure != null -> readErrorHtml(data.failure, RETRY_ID)
            else ->
                """<div class="eb-empty-state">${icon("icon-assignment", "xl")}<h2>등록된 과제가 없어요</h2><p>반이나 학원 전체에 낼 과제를 만들고 자료를 올려 보세요.</p></div>"""
        }

        return """
            <div class="eb-page-header">
                <h1 class="eb-page-title">과제</h1>
                $createButton
            </div>
            <section class="eb-card" aria-label="학원 과제 목록">$list</section>
        """.trimIndent()
    }

    private fun listItemHtml(assignment: Assignment, data: PageData, canManage: Boolean): String {
        val id = assignment.id.escapeHtml()
        val materials = if (assignment.attachments.isEmpty()) {
            """<span class="eb-meta">올린 자료가 없어요</span>"""
        } else {
            assignment.attachments.joinToString("") { attachment ->
                val remove = if (canManage) {
                    """<button class="eb-button eb-button--ghost eb-button--sm remove-material" type="button" data-assignment-id="$id" data-attachment-id="${attachment.id.escapeHtml()}" aria-label="${attachment.fileName.escapeHtml()} 내리기">내리기</button>"""
                } else {
                    ""
                }
                """
                <span class="eb-chip eb-chip--info">
                    <button class="eb-button eb-button--link open-material" type="button" data-assignment-id="$id" data-attachment-id="${attachment.id.escapeHtml()}">${attachment.fileName.escapeHtml()}</button>
                    $remove
                </span>
                """.trimIndent()
            }
        }
        val manageButtons = if (canManage) {
            """
            <button class="eb-button eb-button--ghost eb-button--sm add-material" type="button" data-assignment-id="$id">자료 올리기</button>
            <button class="eb-button eb-button--ghost eb-button--sm edit-assignment" type="button" data-assignment-id="$id">수정</button>
            <button class="eb-button eb-button--ghost eb-button--sm delete-assignment" type="button" data-assignment-id="$id">삭제</button>
            """.trimIndent()
        } else {
            ""
        }

        return """
            <article class="eb-assignment-item">
                ${icon("icon-assignment", "md")}
                <div>
                    <strong>${assignment.title.escapeHtml()}</strong>
                    <span class="eb-meta">${data.className(assignment.classId).escapeHtml()} · 마감 ${assignment.dueDate.escapeHtml()} · ${assignment.teacher.escapeHtml()}</span>
                    <div class="eb-chip-row">$materials</div>
                </div>
                ${if (manageButtons.isEmpty()) "" else """<div class="eb-notice-actions">$manageButtons</div>"""}
            </article>
        """.trimIndent()
    }

    private fun wireList(session: SessionState, academyId: String, data: PageData) {
        (document.getElementById("create-assignment") as? HTMLButtonElement)?.onclick = {
            openEditor(session, academyId, data, existing = null)
            null
        }
        forEachButton(".open-material") { button, assignmentId ->
            button.onclick = {
                val attachmentId = button.getAttribute("data-attachment-id")
                val assignment = data.assignments.firstOrNull { it.id == assignmentId }
                assignment?.attachments?.firstOrNull { it.id == attachmentId }
                    ?.let { openMaterial(academyId, assignment.id, it) }
                null
            }
        }
        forEachButton(".remove-material") { button, assignmentId ->
            button.onclick = {
                val attachmentId = button.getAttribute("data-attachment-id")
                val assignment = data.assignments.firstOrNull { it.id == assignmentId }
                assignment?.attachments?.firstOrNull { it.id == attachmentId }
                    ?.let { confirmRemoveMaterial(session, academyId, assignment, it) }
                null
            }
        }
        forEachButton(".add-material") { button, assignmentId ->
            button.onclick = {
                data.assignments.firstOrNull { it.id == assignmentId }?.let {
                    openMaterialUpload(session, academyId, it)
                }
                null
            }
        }
        forEachButton(".edit-assignment") { button, assignmentId ->
            button.onclick = {
                data.assignments.firstOrNull { it.id == assignmentId }?.let {
                    openEditor(session, academyId, data, it)
                }
                null
            }
        }
        forEachButton(".delete-assignment") { button, assignmentId ->
            button.onclick = {
                data.assignments.firstOrNull { it.id == assignmentId }?.let {
                    openDeleteConfirmation(session, academyId, it)
                }
                null
            }
        }
    }

    private fun forEachButton(selector: String, bind: (HTMLElement, String?) -> Unit) {
        val buttons = document.querySelectorAll(selector)
        for (index in 0 until buttons.length) {
            val button = buttons.item(index) as? HTMLElement ?: continue
            bind(button, button.getAttribute("data-assignment-id"))
        }
    }

    // ---- 자료 ----

    /** 서명 URL은 5분짜리이므로 저장하지 않고 누를 때마다 새로 받는다(게시판과 같은 규약). */
    private fun openMaterial(academyId: String, assignmentId: String, attachment: PostAttachment) {
        scope.launch {
            val ticket = commandRepository.getAssignmentAttachmentUrl(academyId, assignmentId, attachment.id)
            val url = ticket.downloadUrl
            if (!ticket.isSuccess || url == null) {
                Toast.error(ticket.errorMessage ?: "자료를 열지 못했어요. 다시 시도해 주세요.")
                return@launch
            }
            val anchor = document.createElement("a") as HTMLAnchorElement
            anchor.href = url
            anchor.target = "_blank"
            anchor.rel = "noopener"
            document.body?.appendChild(anchor)
            anchor.click()
            anchor.remove()
        }
    }

    private fun openMaterialUpload(session: SessionState, academyId: String, assignment: Assignment) {
        val capacity = MAX_ATTACHMENTS - assignment.attachments.size
        if (capacity <= 0) {
            Toast.error("자료는 과제당 ${MAX_ATTACHMENTS}개까지 올릴 수 있어요.")
            return
        }
        val backdrop = mountModal(
            labelledBy = "material-upload-title",
            """
            <header class="eb-modal__header">
                <h2 id="material-upload-title">자료 올리기</h2>
                <button class="eb-icon-button" id="material-close" type="button" aria-label="닫기">${icon("icon-close", "md")}</button>
            </header>
            <form class="eb-modal__body" id="material-form">
                <p><strong>${assignment.title.escapeHtml()}</strong></p>
                <div class="eb-field">
                    <label for="material-files">자료 파일</label>
                    <input id="material-files" type="file" multiple accept="image/jpeg,image/png,image/webp,application/pdf">
                    <p class="eb-meta">JPEG, PNG, WEBP 또는 PDF · 파일당 10MiB 미만 · 이 과제에 ${capacity}개 더 올릴 수 있어요.</p>
                    <div id="material-file-list" aria-live="polite"></div>
                </div>
                <p class="eb-form-error" role="alert" id="material-error" hidden></p>
            </form>
            <footer class="eb-modal__footer">
                <button class="eb-button eb-button--ghost" id="material-cancel" type="button">취소</button>
                <button class="eb-button eb-button--primary" id="material-submit" type="submit" form="material-form">올리기</button>
            </footer>
            """.trimIndent(),
        )

        var pending = emptyList<PendingMaterial>()
        var saving = false
        // 하나라도 승격에 성공했으면 닫을 때 목록을 다시 읽는다. 안 그러면 올라간 자료가
        // 화면에 없는 채로 남고, 다음 "자료 올리기"의 남은 자리 계산도 낡은 값으로 안내한다.
        var attachedAny = false
        val fileInput = backdrop.querySelector("#material-files") as? HTMLInputElement
        val submit = backdrop.querySelector("#material-submit") as? HTMLButtonElement
        val error = backdrop.querySelector("#material-error") as? HTMLElement

        fun close() {
            if (saving) return
            backdrop.remove()
            if (attachedAny) open(session, academyId)
        }

        fun refresh() {
            renderPendingMaterials(backdrop, pending)
            submit?.disabled = saving || pending.isEmpty()
            // 한 번이라도 승격을 시도한 뒤에는 파일을 다시 고를 수 없다. 목록을 갈아치우면
            // 이미 성공한 항목의 result 가 사라져 재제출 때 같은 파일이 한 번 더 붙는다
            // (게시판 첨부 모달이 같은 이유로 같은 가드를 둔다).
            fileInput?.disabled = saving || pending.any { it.result != null }
        }

        listOf("#material-close", "#material-cancel").forEach { selector ->
            (backdrop.querySelector(selector) as? HTMLElement)?.onclick = { close(); null }
        }
        fileInput?.onchange = {
            if (!saving && pending.none { it.result != null }) {
                pending = selectedFiles(fileInput).map { PendingMaterial(it, AttachmentStagingUploader.newUploadId()) }
                error?.setAttribute("hidden", "")
                refresh()
            }
            null
        }
        (backdrop.querySelector("#material-form") as? HTMLFormElement)?.onsubmit = { event ->
            event.preventDefault()
            val invalid = validateMaterials(pending, capacity)
            if (!saving && invalid != null) {
                showError(error, invalid)
            } else if (!saving) {
                saving = true
                refresh()
                scope.launch {
                    // 한 파일씩 순서대로 올린다. 서버가 개수 상한을 트랜잭션 안에서 세므로
                    // 동시에 밀어 넣으면 마지막 몇 개만 실패하고 그 이유가 화면에 흐려진다.
                    pending.filter { it.result?.isSuccess != true }.forEach { material ->
                        material.result = AttachmentStagingUploader.uploadAndAttachToAssignment(
                            academyId = academyId,
                            assignmentId = assignment.id,
                            file = material.file,
                            uploadId = material.uploadId,
                        )
                    }
                    saving = false
                    attachedAny = attachedAny || pending.any { it.result?.isSuccess == true }
                    refresh()
                    if (pending.all { it.result?.isSuccess == true }) {
                        backdrop.remove()
                        Toast.success("자료를 올렸어요.")
                        open(session, academyId)
                    } else {
                        showError(error, "올리지 못한 자료가 있어요. 파일별 안내를 확인한 뒤 다시 시도해 주세요.")
                    }
                }
            }
            null
        }
        refresh()
    }

    private class PendingMaterial(
        val file: File,
        val uploadId: String,
        var result: AttachmentUploadResult? = null,
    )

    private fun renderPendingMaterials(backdrop: HTMLElement, pending: List<PendingMaterial>) {
        val target = backdrop.querySelector("#material-file-list") as? HTMLElement ?: return
        target.innerHTML = pending.joinToString("") { material ->
            val state = when {
                material.result == null -> ""
                material.result?.isSuccess == true -> """<span class="eb-chip eb-chip--success">올림</span>"""
                else -> """<span class="eb-chip eb-chip--error">${(material.result?.errorMessage ?: "실패").escapeHtml()}</span>"""
            }
            """<p class="eb-meta">${material.file.name.escapeHtml()} $state</p>"""
        }
    }

    private fun validateMaterials(pending: List<PendingMaterial>, capacity: Int): String? = when {
        pending.isEmpty() -> "올릴 자료를 선택해 주세요."
        pending.size > capacity -> "이 과제에는 ${capacity}개까지만 더 올릴 수 있어요."
        pending.any { AttachmentContentType.fromMimeType(it.file.type) == null } ->
            "JPEG, PNG, WEBP 또는 PDF만 올릴 수 있어요."
        pending.any { it.file.size.toDouble() >= MAX_ATTACHMENT_BYTES.toDouble() } ->
            "자료는 각각 10MiB보다 작아야 해요."
        else -> null
    }

    private fun selectedFiles(input: HTMLInputElement?): List<File> {
        val files = input?.files ?: return emptyList()
        return (0 until files.length).mapNotNull { files.item(it) }
    }

    private fun confirmRemoveMaterial(
        session: SessionState,
        academyId: String,
        assignment: Assignment,
        attachment: PostAttachment,
    ) {
        val backdrop = mountModal(
            labelledBy = "material-remove-title",
            """
            <header class="eb-modal__header">
                <h2 id="material-remove-title">자료를 내릴까요?</h2>
                <button class="eb-icon-button" id="material-remove-close" type="button" aria-label="닫기">${icon("icon-close", "md")}</button>
            </header>
            <div class="eb-modal__body">
                <p><strong>${attachment.fileName.escapeHtml()}</strong></p>
                <p>학생·보호자가 더 이상 이 자료를 열 수 없어요. 파일도 함께 삭제돼요.</p>
                <p class="eb-form-error" role="alert" id="material-remove-error" hidden></p>
            </div>
            <footer class="eb-modal__footer">
                <button class="eb-button eb-button--ghost" id="material-remove-cancel" type="button">취소</button>
                <button class="eb-button eb-button--primary" id="material-remove-confirm" type="button">내리기</button>
            </footer>
            """.trimIndent(),
        )
        var submitting = false
        listOf("#material-remove-close", "#material-remove-cancel").forEach { selector ->
            (backdrop.querySelector(selector) as? HTMLElement)?.onclick = {
                if (!submitting) backdrop.remove()
                null
            }
        }
        val confirm = backdrop.querySelector("#material-remove-confirm") as? HTMLButtonElement ?: return
        confirm.onclick = {
            if (!submitting) {
                submitting = true
                confirm.disabled = true
                scope.launch {
                    val result = commandRepository.removeAssignmentAttachment(
                        academyId,
                        assignment.id,
                        attachment.id,
                    )
                    if (result.isSuccess) {
                        backdrop.remove()
                        Toast.success("자료를 내렸어요.")
                        open(session, academyId)
                    } else {
                        submitting = false
                        confirm.disabled = false
                        showError(
                            backdrop.querySelector("#material-remove-error") as? HTMLElement,
                            result.errorMessage ?: "자료를 내리지 못했어요. 다시 시도해 주세요.",
                        )
                    }
                }
            }
            null
        }
        confirm.focus()
    }

    // ---- 등록·수정 ----

    private fun openEditor(
        session: SessionState,
        academyId: String,
        data: PageData,
        existing: Assignment?,
    ) {
        val isCreating = existing == null
        val classOptions = buildString {
            append("""<option value="">학원 전체</option>""")
            data.classes.forEach { classData ->
                append("""<option value="${classData.id.escapeHtml()}">${classData.className.escapeHtml()}</option>""")
            }
        }
        // 대상 반은 등록할 때만 정한다 — 서버 `updateAssignment`가 제목·마감일만 받는다.
        val targetField = if (!isCreating) {
            ""
        } else {
            """
            <div class="eb-field">
                <label for="assignment-class">대상</label>
                <select id="assignment-class">$classOptions</select>
                <p class="eb-meta">반을 고르면 그 반 학생과 보호자에게만 알림이 가요.</p>
            </div>
            """.trimIndent()
        }

        val backdrop = mountModal(
            labelledBy = "assignment-editor-title",
            """
            <header class="eb-modal__header">
                <h2 id="assignment-editor-title">${if (isCreating) "과제 내기" else "과제 수정"}</h2>
                <button class="eb-icon-button" id="assignment-editor-close" type="button" aria-label="닫기">${icon("icon-close", "md")}</button>
            </header>
            <form class="eb-modal__body" id="assignment-editor-form">
                <div class="eb-field">
                    <label for="assignment-title">제목</label>
                    <input id="assignment-title" maxlength="100" value="${existing?.title?.escapeHtml().orEmpty()}" required>
                </div>
                <div class="eb-field">
                    <label for="assignment-due-date">마감일</label>
                    <input id="assignment-due-date" type="date" value="${existing?.dueDate?.escapeHtml().orEmpty()}" required>
                </div>
                $targetField
                ${if (isCreating) """<p class="eb-meta">과제를 만든 뒤 자료(PDF·사진)를 올릴 수 있어요.</p>""" else ""}
                <p class="eb-form-error" role="alert" id="assignment-editor-error" hidden></p>
            </form>
            <footer class="eb-modal__footer">
                <button class="eb-button eb-button--ghost" id="assignment-editor-cancel" type="button">취소</button>
                <button class="eb-button eb-button--primary" id="assignment-editor-submit" type="submit" form="assignment-editor-form">${if (isCreating) "내기" else "저장"}</button>
            </footer>
            """.trimIndent(),
        )

        var isSubmitting = false
        fun close() = backdrop.remove()
        fun setSubmitting(pending: Boolean) {
            isSubmitting = pending
            listOf("#assignment-editor-close", "#assignment-editor-cancel", "#assignment-editor-submit").forEach { selector ->
                (backdrop.querySelector(selector) as? HTMLButtonElement)?.disabled = pending
            }
        }
        listOf("#assignment-editor-close", "#assignment-editor-cancel").forEach { selector ->
            (backdrop.querySelector(selector) as? HTMLElement)?.onclick = {
                if (!isSubmitting) close()
                null
            }
        }

        val form = backdrop.querySelector("#assignment-editor-form") as? HTMLFormElement ?: return
        form.addEventListener("submit", { event ->
            event.preventDefault()
            if (!isSubmitting) {
                submitEditor(session, academyId, existing, backdrop, ::close, ::setSubmitting)
            }
        })
        (backdrop.querySelector("#assignment-title") as? HTMLInputElement)?.focus()
    }

    private fun submitEditor(
        session: SessionState,
        academyId: String,
        existing: Assignment?,
        backdrop: HTMLElement,
        close: () -> Unit,
        setSubmitting: (Boolean) -> Unit,
    ) {
        val title = (backdrop.querySelector("#assignment-title") as? HTMLInputElement)?.value?.trim().orEmpty()
        val dueDate = (backdrop.querySelector("#assignment-due-date") as? HTMLInputElement)?.value?.trim().orEmpty()
        val error = backdrop.querySelector("#assignment-editor-error") as? HTMLElement ?: return
        val submit = backdrop.querySelector("#assignment-editor-submit") as? HTMLButtonElement ?: return
        if (submit.disabled) return

        if (title.isEmpty() || dueDate.isEmpty()) {
            showError(error, "제목과 마감일을 모두 입력해 주세요.")
            return
        }
        if (title.length > 100) {
            showError(error, "제목은 100자까지 입력할 수 있어요.")
            return
        }
        if (existing != null && title == existing.title && dueDate == existing.dueDate) {
            showError(error, "변경된 내용이 없어요.")
            return
        }

        setSubmitting(true)
        scope.launch {
            try {
                val result = if (existing == null) {
                    commandRepository.createAssignment(
                        CreateAssignmentRequest(
                            academyId = academyId,
                            title = title,
                            dueDate = dueDate,
                            classId = (backdrop.querySelector("#assignment-class") as? HTMLSelectElement)
                                ?.value?.takeIf { it.isNotEmpty() },
                        ),
                    )
                } else {
                    commandRepository.updateAssignment(
                        UpdateAssignmentRequest(
                            academyId = academyId,
                            assignmentId = existing.id,
                            title = title.takeIf { it != existing.title },
                            dueDate = dueDate.takeIf { it != existing.dueDate },
                        ),
                    )
                }
                if (result.isSuccess) {
                    close()
                    Toast.success(if (existing == null) "과제를 냈어요. 이제 자료를 올릴 수 있어요." else "과제를 수정했어요.")
                    open(session, academyId)
                } else {
                    showError(error, result.errorMessage ?: "저장에 실패했어요. 다시 시도해 주세요.")
                }
            } finally {
                setSubmitting(false)
            }
        }
    }

    private fun openDeleteConfirmation(session: SessionState, academyId: String, assignment: Assignment) {
        val backdrop = mountModal(
            labelledBy = "assignment-delete-title",
            """
            <header class="eb-modal__header">
                <h2 id="assignment-delete-title">과제를 삭제할까요?</h2>
                <button class="eb-icon-button" id="assignment-delete-close" type="button" aria-label="닫기">${icon("icon-close", "md")}</button>
            </header>
            <div class="eb-modal__body">
                <p><strong>${assignment.title.escapeHtml()}</strong></p>
                <p>삭제한 과제는 학생·보호자에게 더 이상 보이지 않고, 올린 자료도 함께 내려가요.</p>
                <p class="eb-form-error" role="alert" id="assignment-delete-error" hidden></p>
            </div>
            <footer class="eb-modal__footer">
                <button class="eb-button eb-button--ghost" id="assignment-delete-cancel" type="button">취소</button>
                <button class="eb-button eb-button--primary" id="assignment-delete-confirm" type="button">삭제</button>
            </footer>
            """.trimIndent(),
        )

        var isSubmitting = false
        fun close() = backdrop.remove()
        fun setSubmitting(pending: Boolean) {
            isSubmitting = pending
            listOf("#assignment-delete-close", "#assignment-delete-cancel", "#assignment-delete-confirm").forEach { selector ->
                (backdrop.querySelector(selector) as? HTMLButtonElement)?.disabled = pending
            }
        }
        listOf("#assignment-delete-close", "#assignment-delete-cancel").forEach { selector ->
            (backdrop.querySelector(selector) as? HTMLElement)?.onclick = {
                if (!isSubmitting) close()
                null
            }
        }

        val confirm = backdrop.querySelector("#assignment-delete-confirm") as? HTMLButtonElement ?: return
        confirm.onclick = {
            if (!isSubmitting) {
                setSubmitting(true)
                scope.launch {
                    try {
                        val result = commandRepository.deleteAssignment(academyId, assignment.id)
                        if (result.isSuccess) {
                            close()
                            Toast.success("과제를 삭제했어요.")
                            open(session, academyId)
                        } else {
                            showError(
                                backdrop.querySelector("#assignment-delete-error") as? HTMLElement,
                                result.errorMessage ?: "삭제에 실패했어요. 다시 시도해 주세요.",
                            )
                        }
                    } finally {
                        setSubmitting(false)
                    }
                }
            }
            null
        }
        confirm.focus()
    }

    private fun mountModal(labelledBy: String, contentHtml: String): HTMLElement {
        val backdrop = document.createElement("div") as HTMLElement
        backdrop.className = "eb-modal-backdrop"
        backdrop.innerHTML = """
            <section class="eb-modal" role="dialog" aria-modal="true" aria-labelledby="$labelledBy">
                $contentHtml
            </section>
        """.trimIndent()
        document.body?.appendChild(backdrop)
        return backdrop
    }

    private fun showError(element: HTMLElement?, message: String) {
        element ?: return
        element.textContent = message
        element.removeAttribute("hidden")
    }
}
